/**
* @summary - Component that Provides Attachments Tab
* @author Durai Murugan B S
* @since 2024-07-04
*/
import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Box,
    Button,
    Grid,
    Stack,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { DefaultCard } from '../../assets/styled-components/card';
import { getAuthToken } from "../../utility/tokenUtility";
import { BemsGraph, FemsGraph, MasterGraph } from '../../utility/apiURLS';
import CustomDialog from '../commoncomponents/CustomDialog';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import { v4 as uuidv4 } from 'uuid';
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SectionTitle from '../commoncomponents/SectionTitle';
const initialRow = {
    id: 1,
    file: null,
    documenttitle: '',
    filedesc: '',
    filesize: '',
};

const Departmentworkrequestattachments = forwardRef((props, ref) => {
    
    const [rows, setRows] = useState(props.isViewMode ? [] : [initialRow]);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const navigate = useNavigate();
    const token = getAuthToken("id_token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [registeredFiles, setRegisteredFiles] = useState([]);
    const [storeFileName, setStoreFileName] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [deletedFilesArray, setDeletedFilesArray] = useState([]);
    const [deletedRowIds, setDeletedRowIds] = useState([])
    const [previouslyUploadedFiles, setPreviouslyUploadedFiles] = useState([]);

    const[fetchedFiles, setFetchedFiles] = useState([]);

    const location = useLocation();
    const editMode = location?.state?.isEditMode;
    const viewMode = location?.state?.isViewMode;

    const convertFetchedFilesToHandleFileFormat = (fetchedFiles) => {
        // console.log('fetcfetch', fetchedFiles)
        return fetchedFiles.map((file, index) => {
            const { documenttitle, filedesc, filesize, id, filename } = file;
    
            // Mock a file object using Blob
            const fileBlob = new Blob([""], { type: `application/${filedesc}` });
            fileBlob.name = documenttitle;
    
            // Calculate file size
            let fileSizeFormatted;
            const sizeInBytes = parseFloat(filesize) * (filesize.includes('MB') ? 1024 * 1024 : 1024);
            if (sizeInBytes >= 1024 * 1024) {
                fileSizeFormatted = (sizeInBytes / (1024 * 1024)).toFixed(2) + ' MB';
            } else {
                fileSizeFormatted = (sizeInBytes / 1024).toFixed(2) + ' KB';
            }
    
            return {
                id, // Generate unique IDs
                file: fileBlob, // Mocked file object
                documenttitle,
                filedesc,
                filesize: fileSizeFormatted,
                filename
            };
        });
    };
    

    const fetchFiles = async (headerId) => {
        try {
            let response;
            const checkGraph =
                props.service
                    ? props.service === "FEMS" ? FemsGraph : BemsGraph
                    : ((editMode || viewMode) && location?.state?.service === "FEMS")
                        ? FemsGraph
                        : BemsGraph;

            const service =
                props.service
                    ? props.service === "FEMS" ? 'fem' : 'bem'
                    : ((editMode || viewMode) && location?.state?.service === "FEMS")
                        ? 'fem'
                        : 'bem';

            response = await axios.get(`${checkGraph}/api/Graph${service}Departmentrequest/Getdepartmentattachmentlist${service}/${headerId || location?.state?.id}`, config);
            const fetchedFiles = response.data; // Assuming the response is an array of files
            // setRows(props.isViewMode ? fetchedFiles : (fetchedFiles.length > 0 ? fetchedFiles : [initialRow]));

            if (response?.data?.length > 0) {
                const formattedFetchedFiles = convertFetchedFilesToHandleFileFormat(fetchedFiles);

                setRows(formattedFetchedFiles);
                setFetchedFiles(formattedFetchedFiles);
            } else if (props.isViewMode == true) {
                setRows(fetchedFiles);
            }
            else {
                const newRow = {
                    id: 1,
                    category: '',
                    fileType: '',
                    designation: '',
                    size: '',
                };
                setRows([newRow]);
            }

        } catch (error) {
            console.error('Error fetching files:', error);
        }
    };

    useEffect(() => {
        if (editMode || viewMode)
            // console.log('ed', location?.state?.id)
        fetchFiles(location?.state?.id);
    }, [location?.state?.id])


    const handleDeleteClick = (id) => () => {
        // Find the row to delete by its ID
        const rowToDelete = rows.find(row => row.id === id);

        // Check if either file is null or category is an empty string
        if (rowToDelete && (rowToDelete.file === null || rowToDelete.category === "")) {
            // If either condition is met, delete immediately without confirmation
            const updatedRows = rows.filter((row) => row.id !== id);
            setRows(updatedRows);

            // Move the deleted file to `deletedFilesArray` if applicable
            const fileToDelete = selectedFiles.find((file) => file.id === id);
            if (fileToDelete) {
                setDeletedFilesArray((prevDeletedFiles) => [...prevDeletedFiles, fileToDelete]);  // Add to deleted files array
            }

            // Remove the deleted file from `selectedFiles`
            const updatedSelectedFiles = selectedFiles.filter((file) => file.id !== id);
            setSelectedFiles(updatedSelectedFiles);  // Update selectedFiles array

            // Remove the file from `previouslyUploadedFiles` if it was already uploaded
            const updatedPreviouslyUploadedFiles = previouslyUploadedFiles.filter(file => file.id !== id);
            setPreviouslyUploadedFiles(updatedPreviouslyUploadedFiles);

            // If no rows are left, add a default empty row
            if (updatedRows.length === 0) {
                setRows([initialRow]);  // Add default empty row if no rows are left
            }
        } else {
            // If neither condition is met, open the delete confirmation dialog
            setSelectedId(id);
            setDeleteDialogOpen(true);
        }
    };


    const handleDeleteConfirm = async () => {
        const selectedRow = rows.find(row => row.id === selectedId);


        if (selectedRow) {
            try {
                if (typeof selectedId === 'string') {
                    const checkGraph =
                        props.service
                            ? props.service === "FEMS" ? FemsGraph : BemsGraph
                            : (editMode && location?.state?.service === "FEMS")
                                ? FemsGraph
                                : BemsGraph;

                    const service =
                        props.service
                            ? props.service === "FEMS" ? 'fem' : 'bem'
                            : (editMode && location?.state?.service === "FEMS")
                                ? 'fem'
                                : 'bem';

                    const response = await axios.delete(`${checkGraph}/api/${service}departmentworkrequestattachments/${selectedId}`, config);

                    if (response.status >= 200 && response.status <= 204) {
                        const updatedRows = rows.filter((row) => row.id !== selectedId);
                        setRows(updatedRows);
                        const updatedSelectedFiles = selectedFiles.filter(file => file.id !== selectedId);
                        setSelectedFiles(updatedSelectedFiles);
                        // fetchFiles();
                    } else {
                        throw new Error('Failed to delete record');
                    };
                } else if (typeof selectedId === 'number') {
                    const updatedRows = rows.filter((row) => row.id !== selectedId);

                    if (updatedRows.length === 0) {
                        // If no rows remain, set the initial row
                        setRows([initialRow]);
                    } else {
                        // Set the updated rows
                        setRows(updatedRows);
                    }

                    // Update the selected files array
                    const updatedSelectedFiles = selectedFiles.filter(file => file.id !== selectedId);
                    setSelectedFiles(updatedSelectedFiles);

                    // Optional: Fetch files if rows are cleared
                    if (updatedRows.length === 0) {
                        fetchFiles();
                    }
                }
            } catch (error) {
                console.error('Error deleting attachment:', error);
                setError(true);
                setErrorMessage('Error deleting attachment. Please try again.');
            }
        }
        setDeleteDialogOpen(false);
    };
    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
    };

    const handleSuccessDialogClose = () => {
        setSuccessDialogOpen(false);
    };

    const handleAddRow = () => {
        const id =
            rows.length > 0
                ? Math.max(...rows.map((row) => (isNaN(row.id) ? 0 : Number(row.id)))) + 1
                : 1;
        const newRow = {
            id: id,
            file: null,
            documenttitle: '',
            filedesc: '',
            filesize: '',
        };
        setRows([...rows, newRow]);
    };

    // const handleFileChange = (event, id) => {
    //     const file = event.target.files[0];
    //     if (!file) return;
    //     const fileName = file.name;
    //     const fileExtension = fileName.split('.').pop().toLowerCase();
    //     if (file.size > 20 * 1024 * 1024) {
    //         setError(true);
    //         setErrorMessage('Error: File size exceeds 20 MB. Please select smaller files.');
    //         return;
    //     }

    //     const allowedFileExtensions = ['doc', 'jpeg', 'jpg', 'png', 'xlsx', 'csv', 'pdf', 'docx'];
    //     if (!allowedFileExtensions.includes(fileExtension)) {
    //         setError(true);
    //         setErrorMessage('Error: Only DOC, JPEG, JPG, PNG, XLSX, CSV, and PDF files are allowed.');
    //         return;
    //     }

    //     const fileNameWithoutExtension = fileName.replace(new RegExp(`.${fileExtension}$`), '');

    //     let updatedSelectedFiles = [...selectedFiles];
    //     const fileIndex = updatedSelectedFiles.findIndex((file) => file.id === id);
    //     if (fileIndex > -1) {
    //         updatedSelectedFiles[fileIndex] = { id, file };
    //     } else {
    //         updatedSelectedFiles.push({ id, file });
    //     }
    //     setSelectedFiles(updatedSelectedFiles);

    //     const updatedRows = rows.map((row) => {
    //         if (row.id === id) {
    //             let fileSize;
    //             if (file.size >= 1024 * 1024) {
    //                 fileSize = (file.size / (1024 * 1024)).toFixed(2) + ' MB';
    //             } else {
    //                 fileSize = (file.size / 1024).toFixed(2) + ' KB';
    //             }

    //             return {
    //                 ...row,
    //                 file,
    //                 documenttitle: fileName,
    //                 filedesc: fileExtension,
    //                 filesize: fileSize,
    //             };
    //         }
    //         return row;
    //     });

    //     setRows(updatedRows);
    //     setHasUnsavedChanges(true);

    //     // Debug: log the updated rows state
    // };


    const handleFileChange = (event, rowId) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const allowedFileExtensions = ['doc', 'jpeg', 'jpg', 'png', 'xlsx', 'csv', 'pdf', 'docx'];

    let newRows = [...rows];
    let newSelectedFiles = [...selectedFiles];

    // ✅ REMOVE empty placeholder rows (that have no file)
    newRows = newRows.filter(row => row.file !== null && row.documenttitle !== '');

    let maxId = Math.max(0, ...newRows.map(row => typeof row.id === "number" ? row.id : 0));

    files.forEach((file, index) => {
        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (file.size > 20 * 1024 * 1024) {
            setError(true);
            setErrorMessage(`Error: ${file.name} exceeds 20 MB.`);
            return;
        }

        if (!allowedFileExtensions.includes(fileExtension)) {
            setError(true);
            setErrorMessage(`Error: ${file.name} is not an allowed file type.`);
            return;
        }

        const fileSize = file.size >= 1024 * 1024
            ? (file.size / (1024 * 1024)).toFixed(2) + ' MB'
            : (file.size / 1024).toFixed(2) + ' KB';

        const newId = ++maxId;

        newRows.push({
            id: newId,
            file,
            documenttitle: file.name,
            filedesc: fileExtension,
            filesize: fileSize,
        });

        newSelectedFiles.push({ id: newId, file });
    });

    setRows(newRows);
    setSelectedFiles(newSelectedFiles);
    setHasUnsavedChanges(true);
};


    const handleDownload = async (file) => {
        // console.log('filedown', file)
        try {
            // Perform the GET request to download the file
            const response = await axios.get(`${MasterGraph}/api/FileUpload/DownloadFile/${file.filename}`, {
                ...config,
                responseType: 'blob'
            });

            if (response) {
                const url = URL.createObjectURL(response.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = file.documenttitle || file.filename; // Use documenttitle as the filename
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }
            else {
                if (file && file instanceof Blob) {
                    const url = URL.createObjectURL(file);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = file.documenttitle || file.filename; // Use documenttitle as the filename
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                } else {
                    console.error('Invalid file object:', file);
                }
            }
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };



    const handleDownloadClick = (event, file) => {
        event.preventDefault();
        handleDownload(file);
    };
    function uuidv4(extension) {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        }) + '.' + extension;
    }

    const uploadFiles = async (selectedFiles) => {
        try {
            let storeFileNameArray = [];
            let storeregsitredFilesResponse = [];
            let storefileUploadResponse = [];
            for (let i = 0; i < selectedFiles?.length; i++) {
                const formData = new FormData();
                const extension = selectedFiles[i]?.file?.name.split('.').pop();
                const fileName = uuidv4(extension); // generate a unique id
                formData.append('fileObj', selectedFiles[i]?.file, fileName); // append file with unique id as filename
                storeFileNameArray.push({ id: fileName, filename: `${selectedFiles[i]?.file?.name}`, selectedFileId: selectedFiles[i]?.id });

                try {
                    const fileUploadResponse = await axios.post(`${MasterGraph}/api/fileupload`, formData, config);
                    storeregsitredFilesResponse.push(fileUploadResponse.data);
                    storefileUploadResponse.push(fileUploadResponse.data);
                } catch (error) {
                    console.log('Error uploading file:', error.response ? error.response.data : error.message);
                }
            }
            setStoreFileName(storeFileNameArray);
            setRegisteredFiles(storeregsitredFilesResponse);
            return { storefileUploadResponse, storeFileNameArray };
        } catch (error) {
            console.error('Save error:', error);
            throw error;
        }
    };


    const saveFilesToAttachment = async (payload) => {
        try {
            let response;
            const checkGraph =
                props.service
                    ? props.service === "FEMS" ? FemsGraph : BemsGraph
                    : (editMode && location?.state?.service === "FEMS")
                        ? FemsGraph
                        : BemsGraph;

            const service =
                props.service
                    ? props.service === "FEMS" ? 'fem' : 'bem'
                    : (editMode && location?.state?.service === "FEMS")
                        ? 'fem'
                        : 'bem';

            // console.log('checkGraph', checkGraph)
            // console.log('service', service)
            response = await axios.post(`${checkGraph}/api/${service}departmentworkrequestattachments`, payload, config);


            if (response.status >= 200 && response.status <= 204) {
                setOpenDialog(true);
            }
            return response.data;
        } catch (error) {
            console.error('Save error:', error);
            throw error;
        }
    };

    const handleSaveClick = async (service1,id) => {
        try {
            let allFiles = [];

            if (service1 === location?.state?.service) {
                // If service matches location.state.service, only include selectedFiles
                allFiles = [...selectedFiles];
            } else {
                // Otherwise, include both fetchedFiles and selectedFiles
                allFiles = [...fetchedFiles, ...selectedFiles];
            }
            const serviceForAssetChange = service1 === 'FEMS' ? 'fem' : 'bem';
            
    // console.log('allFiles',allFiles)
            // Filter out duplicates and already uploaded files
            const uniqueFilesToUpload = allFiles
                .filter((file, index, self) =>
                    index === self.findIndex((f) => f.id === file.id) // Deduplicate by ID
                )
                .filter(file => !previouslyUploadedFiles.some(uploaded => uploaded.id === file.id)); // Skip already uploaded files

                // console.log('uniqueFilesToUpload',uniqueFilesToUpload)
    
            // Proceed with file upload if there are files to upload
            if (uniqueFilesToUpload.length > 0) {
                const { storefileUploadResponse, storeFileNameArray } = await uploadFiles(uniqueFilesToUpload);
    // console.log('storefileUploadResponse',storefileUploadResponse)
                if (storefileUploadResponse?.length > 0) {
                    let storefileUploadResponseFinal = [];
    
                    for (let i = 0; i < storefileUploadResponse.length; i++) {
                        const file = storefileUploadResponse[i];
                        const documentTitle = storeFileNameArray.find((item) => item.id === file.fileName)?.filename;
                        const filedesc = documentTitle.split('.').pop().toLowerCase();
    
                        const service = props.service
                            ? props.service === "FEMS" ? 'fem' : 'bem'
                            : (editMode && location?.state?.service === "FEMS") ? 'fem' : 'bem';
    
                        if (documentTitle) {
                            const payload = {
                                filename: file.fileName,
                                filesize: file.fileSize,
                                filepath: "D:/IFAC-X/UploadFolder",
                                documenttitle: documentTitle,
                                filedesc: filedesc,
                                [`${service || serviceForAssetChange}departmentworkrequestheaderId`]: id
                            };
    
                            try {
                                const response = await saveFilesToAttachment(payload);
                                setHasUnsavedChanges(false);
    
                                if (response) {
                                    storefileUploadResponseFinal.push(response);
                                    fetchFiles(id); // Reload files to update the UI
                                } else {
                                    console.log("Error saving file:", file.fileName);
                                }
                            } catch (error) {
                                console.log('Error saving file:', error);
                            }
                        } else {
                            console.log("Document Title not found for file:", file.fileName);
                        }
                    }
    
                    if (storefileUploadResponseFinal.length === storefileUploadResponse.length) {
                        setOpenDialog(true);
    
                        // Add successfully uploaded files to `previouslyUploadedFiles`
                        setPreviouslyUploadedFiles(prev => [...prev, ...uniqueFilesToUpload]);
                    } else {
                        console.log("Error in uploading some files");
                    }
                } else {
                    console.log("No files registered");
                }
            } else {
                console.log("No files selected for upload");
            }
    
            // Handle deletions (remove files from the server)
            if (deletedRowIds.length > 0) {
                for (let id of deletedRowIds) {
                    try {
                        const checkGraph =
                            props.service
                                ? props.service === "FEMS" ? FemsGraph : BemsGraph
                                : (editMode && location?.state?.service === "FEMS")
                                    ? FemsGraph
                                    : BemsGraph;
    
                        const service =
                            props.service
                                ? props.service === "FEMS" ? 'fem' : 'bem'
                                : (editMode && location?.state?.service === "FEMS")
                                    ? 'fem'
                                    : 'bem';
    
                        await axios.delete(`${checkGraph}/api/${service}departmentworkrequestattachments/${id}`, config);
    
                    } catch (error) {
                        console.error(`Error deleting row with ID ${id}:`, error);
                    }
                }
    
                setDeletedRowIds([]);
                setSuccessMessage('Data saved successfully.');
                setSuccessDialogOpen(true);
            }
    
            // Clear deleted files after saving
            if (deletedFilesArray.length > 0) {
                setDeletedFilesArray([]);
            }
    
        } catch (error) {
            console.log('Error registering files:', error);
        }
    };
    
    useImperativeHandle(ref, () => ({
        handleSaveClick,
        fetchFiles,
        clear() {
            const newRow = {
                id: 1,
                category: '',
                fileType: '',
                designation: '',
                size: '',
            };
            setRows([newRow]); // Reset attachments state
        }
    }));


    const handleCancel = () => {
        if (hasUnsavedChanges && !props.isViewMode) {
            setCancelDialogOpen(true);
        } else {
            navigate(-1, { replace: true });
        }
    };

    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
    };

    const handleCancelDialogConfirm = () => {
        setCancelDialogOpen(false);
        navigate(-1, { replace: true });
    };


    const handleSnackbarClose = () => {
        setError(false);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    return (
        <>
            <DefaultCard sx={{ height: '100%' }}>
                <form style={{ position: 'relative' }}>

                    <Stack>
                        <SectionTitle title={"Attachments"} />
                        <Typography variant="labelSm" mt={0}>
                            Please add files in format of doc, jpg, png, pdf, xlsx, csv and
                            file size should be less than 20 MB.
                        </Typography>
                    </Stack>
                    {!location?.state?.isViewMode && (
                        <Stack direction={"row"} justifyContent={"end"}>
                            <Stack mb={1}>
                                <Button variant="contained" onClick={handleAddRow}>
                                    <AddRoundedIcon />
                                    Add File
                                </Button>
                            </Stack>
                        </Stack>
                    )}

                    <CustomAgGrid

                        rows={rows}
                        columns={[
                            {
                                field: 'file',
                                headerName: 'File',
                                flex: 1,
                                cellRenderer: (params) => (
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        disabled={params?.data?.documenttitle || viewMode}
                                        sx={{
                                            width: '100px',
                                            height: '38px',
                                            padding: '4px 8px',
                                            fontSize: '0.75rem',
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {params.data.documenttitle ? 'FILE UPLOADED' : 'BROWSE FILE'}
                                        <input
                                            type="file"
                                            hidden
                                            multiple
                                            onChange={(event) => handleFileChange(event, params?.data?.id)}
                                        />
                                    </Button>
                                ),
                            },
                            {
                                field: 'documenttitle',
                                headerName: 'File Name',
                                flex: 1,
                            },
                            {
                                field: 'filedesc',
                                headerName: 'File Type',
                                flex: 1,
                            },
                            {
                                field: 'filesize',
                                headerName: 'Size',
                                flex: 1,
                            },
                            {
                                field: 'actions',
                                headerName: 'Actions',
                                flex: 1,
                                cellClassName: 'actions',
                                cellRenderer: (params) => (
                                    <>
                                        <Button onClick={handleDeleteClick(params?.data?.id)} disabled={location?.state?.isViewMode || ((rows?.length === 1 && params?.data?.id && !params?.data?.documenttitle) ? true : false)}>
                                            <DeleteIcon />
                                        </Button>
                                        <Button onClick={(event) => handleDownloadClick(event, params?.data)}>
                                            {typeof (params?.data?.id) === 'string' ? <DownloadIcon /> : <></>}
                                        </Button>
                                    </>
                                ),
                            },
                        ]}
                        setGridApi={setGridApi}
                    />
                </form>
            </DefaultCard>

            {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="contained" color="primary" sx={{ m: 1 }} onClick={handleSaveClick} disabled={props.isViewMode}>
                        Save
                    </Button>
                    <Button variant="outlined" sx={{ m: 1 }} onClick={handleCancel}>
                        Back
                    </Button>
                </Box> */}
            <CustomDialog
                open={error}
                onClose={handleSnackbarClose}
                content={errorMessage}
                confirmBtn="OK"
                onConfirm={() => setError(false)}
            />
            {/* <CustomDialog
                open={openDialog}
                onClose={handleCloseDialog}
                content="Files have been uploaded successfully."
                confirmBtn="OK"
                onConfirm={handleCloseDialog}
            /> */}
            {/* <CustomDialog
                open={cancelDialogOpen}
                onClose={handleCancelDialogClose}
                title="Unsaved Changes"
                content="Record(s) not Saved/Updated. Do you want to continue?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={handleCancelDialogConfirm}
                onCancel={handleCancelDialogClose}
            /> */}
            <CustomDialog
                open={deleteDialogOpen}
                onClose={handleDeleteDialogClose}
                title="Confirm Delete"
                content="Are you sure you want to delete?"
                confirmBtn="Yes"
                cancelBtn="No"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteDialogClose}
            />

            <CustomDialog
                open={successDialogOpen}
                onClose={handleSuccessDialogClose}
                content={successMessage}
                confirmBtn="OK"
                onConfirm={handleSuccessDialogClose}
            />

        </>
    );
})

export default Departmentworkrequestattachments;
